// TemplatesPage.jsx
// Landing Page: Templates & Export - Digital Album Ownership
// Slug: /templates

import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './LandingPages.css';

// SEO Meta Tags
const SEO = {
  title: 'Travel Album Templates & High-Res PDF Export | VivaJour',
  description: 'Professional travel album templates designed for storytelling. Export as high-resolution PDFs you own forever, or share via link. Multiple layouts for every type of trip. On-device processing.',
  slug: '/templates'
};

export default function TemplatesPage() {
  useEffect(() => {
    document.title = SEO.title;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = SEO.description;
  }, []);

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-nav-logo">
            <span>VivaJour</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/privacy-first" className="landing-nav-link">Privacy</Link>
            <Link to="/current-trip" className="landing-nav-link">Current Trip</Link>
            <Link to="/albums" className="landing-nav-link">Albums</Link>
            <Link to="/how-it-works" className="landing-nav-link">How It Works</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="landing-hero">
        <span className="landing-hero-badge">🎨 Professional Design</span>
        <h1>Templates You Own<br />Forever</h1>
        <p className="landing-hero-subtitle">
          Professional album layouts designed for travel storytelling. Export as high-resolution PDFs you control completely, share via link, or view as an auto-playing story. Your photos, your files, forever.
        </p>
        <div className="landing-cta-group">
          <Link to="/how-it-works" className="landing-btn landing-btn-primary">
            See How It Works →
          </Link>
        </div>
        <div className="landing-trust-row">
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            High-res export
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            Multiple formats
          </span>
          <span className="landing-trust-item">
            <svg viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
            Templates included
          </span>
        </div>
      </section>

      {/* Template Categories */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Template Styles</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 40px' }}>
            Layouts designed for different trip types and preferences.
          </p>
          
          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📖</div>
              <h3>Classic Journal</h3>
              <p>Clean, timeless design with generous white space. Photos with captions in a traditional photo album style.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🗺️</div>
              <h3>Adventure Log</h3>
              <p>Dynamic layouts with map accents and location headers. Great for road trips, hiking adventures, and multi-destination journeys.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">✨</div>
              <h3>Minimal Modern</h3>
              <p>Full-bleed photos with minimal text. Let your images speak. Ideal for photography-focused travelers.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">👨‍👩‍👧‍👦</div>
              <h3>Family Story</h3>
              <p>Warm layouts with room for longer captions and notes. Designed for sharing with extended family.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📱</div>
              <h3>Story Format</h3>
              <p>Vertical layouts optimized for phone viewing and social sharing. Quick highlights of your trip.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">💾</div>
              <h3>Archival Quality</h3>
              <p>High-resolution layouts designed for long-term digital archiving and maximum detail preservation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Export Options */}
      <section className="landing-section">
        <div className="landing-container">
          <h2>Export & Share Options</h2>
          <p className="landing-section-subtitle">
            Multiple ways to own and share your finished album.
          </p>
          
          <div className="landing-steps" style={{ marginTop: '40px' }}>
            <div className="landing-step">
              <div className="landing-step-number" style={{ background: 'linear-gradient(135deg, #ef4444, #f97316)' }}>📄</div>
              <div className="landing-step-content">
                <h3>High-Res PDF Download</h3>
                <p>Download your album as a high-resolution PDF file you own forever. Keep it on your computer, archive in cloud storage you control, or do whatever you want with it – it's your file.</p>
                <ul style={{ marginTop: '12px', fontSize: '14px', color: 'var(--landing-muted)' }}>
                  <li style={{ marginBottom: '4px' }}>• Standard resolution (for screens)</li>
                  <li style={{ marginBottom: '4px' }}>• High resolution (for archival)</li>
                  <li style={{ marginBottom: '4px' }}>• Pro: Print-quality export</li>
                </ul>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number" style={{ background: 'linear-gradient(135deg, #8b5cf6, #a855f7)' }}>🔗</div>
              <div className="landing-step-content">
                <h3>Shareable Link</h3>
                <p>Generate a private link to share your album with anyone. Recipients can view in their browser – no app required. You control when to deactivate the link.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number" style={{ background: 'linear-gradient(135deg, #22c55e, #10b981)' }}>📱</div>
              <div className="landing-step-content">
                <h3>Story Mode</h3>
                <p>View your album as an auto-playing story. Perfect for showing off your trip on a phone or casting to a TV. Swipe or let it play automatically.</p>
              </div>
            </div>
            
            <div className="landing-step">
              <div className="landing-step-number" style={{ background: 'linear-gradient(135deg, #0ea5e9, #38bdf8)' }}>💾</div>
              <div className="landing-step-content">
                <h3>Local Save</h3>
                <p>Keep your album on device. Access anytime without exporting. Perfect for showing people directly on your phone.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What You Can Do With Your PDFs */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <h2 style={{ textAlign: 'center' }}>Your Files. Your Choice.</h2>
          <p className="landing-section-subtitle" style={{ textAlign: 'center', margin: '0 auto 32px' }}>
            Once you download your album, it's yours to use however you want.
          </p>
          
          <div className="landing-features-grid">
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🖥️</div>
              <h3>View On Any Device</h3>
              <p>Open on Mac, Windows, iPad, Android – PDFs work everywhere.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">☁️</div>
              <h3>Your Cloud Storage</h3>
              <p>Archive in Dropbox, Google Drive, iCloud – wherever you want.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">✉️</div>
              <h3>Email To Family</h3>
              <p>Attach and send – no special apps required for recipients.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">💿</div>
              <h3>Burn To Disc</h3>
              <p>Create physical backups on DVD/Blu-ray for archival.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">📺</div>
              <h3>Display On TV</h3>
              <p>Open on smart TV, Apple TV, Chromecast – perfect for family viewing.</p>
            </div>
            
            <div className="landing-feature-card">
              <div className="landing-feature-icon">🖨️</div>
              <h3>Print If You Want</h3>
              <p>Pro tier includes print-quality PDFs. Use any print service you prefer.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Quality & Privacy */}
      <section className="landing-section">
        <div className="landing-container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '32px' }}>
            <div className="landing-highlight-box">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>📐</span>
                High Resolution
              </h3>
              <ul style={{ marginTop: '16px' }}>
                <li>Standard: Optimized for screens</li>
                <li>High-res: Archival quality</li>
                <li>Pro: Print-ready (300 DPI)</li>
                <li>Multiple export sizes</li>
              </ul>
            </div>
            
            <div className="landing-highlight-box">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '24px' }}>🔒</span>
                Privacy Preserved
              </h3>
              <ul style={{ marginTop: '16px' }}>
                <li>PDFs generated on your device</li>
                <li>No cloud processing for export</li>
                <li>Shareable links are opt-in only</li>
                <li>You control all sharing</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>Simple, Honest Pricing</h2>
          
          <div className="landing-highlight-box" style={{ marginTop: '32px', textAlign: 'center', border: '2px solid #0ea5e9' }}>
            <span style={{ 
              display: 'inline-block',
              background: 'linear-gradient(135deg, #ef4444, #f97316)',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 700,
              padding: '6px 14px',
              borderRadius: '100px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '16px'
            }}>Limited Time Offer</span>
            
            <h3 style={{ marginBottom: '8px', fontSize: '18px' }}>VivaJour Pro</h3>
            
            <div style={{ margin: '20px 0' }}>
              <p style={{ fontSize: '48px', fontWeight: 800, color: 'var(--landing-text)', margin: 0, lineHeight: 1 }}>
                $19.99
                <span style={{ fontSize: '20px', fontWeight: 400, color: 'var(--landing-muted)' }}>/year</span>
              </p>
              <p style={{ fontSize: '16px', color: 'var(--landing-muted)', marginTop: '8px' }}>
                <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>$39.99</span> — Save 50%
              </p>
            </div>
            
            <div style={{ 
              background: 'rgba(14, 165, 233, 0.1)', 
              border: '1px solid rgba(14, 165, 233, 0.3)',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ fontSize: '15px', fontWeight: 600, color: '#0ea5e9', margin: 0 }}>
                🎉 30-Day Free Trial
              </p>
              <p style={{ fontSize: '13px', color: 'var(--landing-muted)', marginTop: '4px', marginBottom: 0 }}>
                Try everything free for 30 days. Cancel anytime to avoid charges.
              </p>
            </div>
            
            <ul style={{ marginTop: '24px', textAlign: 'left', fontSize: '14px', color: 'var(--landing-muted)' }}>
              <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span>Unlimited albums</span>
              </li>
              <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span>High-res PDF downloads you own forever</span>
              </li>
              <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span>All templates included</span>
              </li>
              <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span>Share via link</span>
              </li>
              <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span>Print-quality exports (300 DPI)</span>
              </li>
              <li style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ color: '#22c55e', fontSize: '16px' }}>✓</span>
                <span>On-device processing (complete privacy)</span>
              </li>
            </ul>
            
            <div style={{ marginTop: '28px' }}>
              <a href="https://apps.apple.com/app/id6756980207" className="landing-btn landing-btn-primary" style={{ width: '100%', justifyContent: 'center' }} target="_blank" rel="noopener noreferrer">
                Start Free Trial →
              </a>
            </div>
            
            <p style={{ marginTop: '16px', fontSize: '12px', color: '#64748b', fontStyle: 'italic' }}>
              Cancel anytime. Keep all albums you've created.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section">
        <div className="landing-container-narrow">
          <h2 style={{ textAlign: 'center' }}>Frequently Asked Questions</h2>
          
          <div className="landing-faq">
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What happens if I cancel my subscription?</h4>
              <p className="landing-faq-answer">You keep all albums you've already created and all PDFs you've downloaded. They're your files forever. You just can't create new albums until you resubscribe.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Are my albums locked to VivaJour?</h4>
              <p className="landing-faq-answer">No. You download standard PDF files that open anywhere. We don't use proprietary formats or DRM. Your albums are truly yours.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">What's the difference between resolutions?</h4>
              <p className="landing-faq-answer">Standard resolution (included in all tiers) looks great on screens. High-res is better for archival. Pro tier adds print-quality exports (300 DPI) if you ever want physical copies.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">How long do shareable links last?</h4>
              <p className="landing-faq-answer">Links stay active until you deactivate them. You can generate new links or turn off sharing anytime from your album settings.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Can I preview before exporting?</h4>
              <p className="landing-faq-answer">Yes. Full preview of your album with the selected template before any export. See exactly what you'll get.</p>
            </div>
            
            <div className="landing-faq-item">
              <h4 className="landing-faq-question">Do I need Pro tier for printing?</h4>
              <p className="landing-faq-answer">Only if you want 300 DPI print-quality exports. Standard and high-res tiers can produce good results with many print services, but Pro tier ensures professional printing quality.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="landing-section landing-section-alt">
        <div className="landing-container">
          <div className="landing-callout">
            <h3>Own Your Travel Albums Forever</h3>
            <p>Try free for 30 days. Then $19.99/year (limited time — regular $39.99).</p>
            <div className="landing-cta-group">
              <a href="https://apps.apple.com/app/id6756980207" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on iOS
              </a>
              <a href="https://play.google.com/store/apps/details?id=com.yourco.traveltracker" className="landing-btn landing-btn-primary" target="_blank" rel="noopener noreferrer">
                Start Free Trial on Android
              </a>
            </div>
          </div>
          
          {/* Internal Links */}
          <div className="landing-internal-links">
            <Link to="/albums" className="landing-internal-link">
              📚 Albums — Automatic trip organization
            </Link>
            <Link to="/current-trip" className="landing-internal-link">
              📍 Current Trip — Quick daily digests
            </Link>
            <Link to="/how-it-works" className="landing-internal-link">
              ⚙️ How It Works — See the full process
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer-links">
          <Link to="/privacy-first" className="landing-footer-link">Privacy</Link>
          <Link to="/current-trip" className="landing-footer-link">Current Trip</Link>
          <Link to="/albums" className="landing-footer-link">Albums</Link>
          <Link to="/how-it-works" className="landing-footer-link">How It Works</Link>
          <Link to="/templates" className="landing-footer-link">Templates</Link>
        </div>
        <p className="landing-footer-meta">
          VivaJour Labs LLC • Built in Wisconsin
        </p>
        <p className="landing-footer-meta" style={{ marginTop: '8px' }}>
          <Link to="/privacy-policy" className="landing-footer-link">Privacy Policy</Link>
          {' • '}
          <Link to="/terms" className="landing-footer-link">Terms of Service</Link>
          {' • '}
          <Link to="/contact" className="landing-footer-link">Contact</Link>
        </p>
        <p className="landing-footer-tagline">
          © 2026 VivaJour. Your albums stay yours. Forever.
        </p>
      </footer>
    </div>
  );
}
